"use client";

import React, { useState } from "react";
import dynamic from "next/dynamic";
import { BookOpen, FileText, Lock } from "lucide-react";

// react-pdf touches `window` at import time — client-only.
const PageByPagePdfViewer = dynamic(
  () =>
    import("@/components/pdf-viewer/PageByPagePdfViewer").then(
      (m) => m.PageByPagePdfViewer,
    ),
  { ssr: false },
);

interface ProtectedPdfListProps {
  postId: string;
  /** Titles only — the file URLs/keys never reach the browser. */
  titles: string[];
  watermark?: string;
  currentLanguage?: "en" | "mm";
}

/**
 * Protected `pdfBlock` renderer. Each PDF opens in the page-by-page,
 * server-watermarked viewer through `/api/media/pdf-proxy?post=…&idx=…`;
 * the proxy re-reads the post AS the visitor, so the post's visibility /
 * allowedRoles are the access check. No iframe, no download link.
 */
export function ProtectedPdfList({
  postId,
  titles,
  watermark,
  currentLanguage = "en",
}: ProtectedPdfListProps) {
  const [openIdx, setOpenIdx] = useState<number | null>(null);
  const readLabel = currentLanguage === "mm" ? "ဖတ်ရန်" : "Read";
  const note =
    currentLanguage === "mm"
      ? "အွန်လိုင်းတွင်သာ ဖတ်ရှုနိုင်ပါသည်"
      : "Read online only";

  return (
    <div data-post-body="pdf-protected" className="space-y-2">
      <ul className="space-y-2">
        {titles.map((title, i) => (
          <li
            key={`${postId}-${i}`}
            className="flex items-center justify-between gap-3 rounded-md border bg-card px-4 py-3"
          >
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="h-4 w-4 text-primary shrink-0" />
              <span className="text-sm font-medium truncate">{title}</span>
            </div>
            <button
              type="button"
              onClick={() => setOpenIdx(i)}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground shadow-sm shrink-0"
            >
              <BookOpen className="h-3.5 w-3.5" />
              {readLabel}
            </button>
          </li>
        ))}
      </ul>
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Lock className="h-3 w-3" />
        {note}
      </p>

      {openIdx !== null && (
        <PageByPagePdfViewer
          pdfUrl={`/api/media/pdf-proxy?post=${encodeURIComponent(postId)}&idx=${openIdx}`}
          title={titles[openIdx]}
          watermark={watermark}
          bookId={`${postId}-${openIdx}`}
          onClose={() => setOpenIdx(null)}
        />
      )}
    </div>
  );
}

export default ProtectedPdfList;
